import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import styled, { keyframes } from 'styled-components';

import { AdvancedError } from '../../common/Error/AdvancedError';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';

import { calculateDrawerOffset } from './calculateDrawerOffset';
import { DRAWER_TRANSITION_DURATION_MS } from './drawer.constants';
import { Layer } from './Layer';
import { LayerStatus } from './types/LayerStatus';
import { useDrawer } from './useDrawer.hook';

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;
const fadeOut = keyframes`
    from { opacity: 1; }
    to { opacity: 0; }
`;

const Wrapper = styled(Box)`
    position: fixed;
    inset: 0;
    z-index: ${theme.zIndex.drawer};
`;

const Backdrop = styled(Box)<{ $isClosing: boolean }>`
    position: fixed;
    inset: 0;
    pointer-events: auto;
    background-color: ${theme.colors.background.dimming};
    z-index: 0;
    animation: ${({ $isClosing }) => ($isClosing ? fadeOut : fadeIn)} ${DRAWER_TRANSITION_DURATION_MS}ms ease-in-out
        forwards;
`;

export const Drawer = () => {
    const { layers, requestClose } = useDrawer();
    const hasLayers = layers.length > 0;
    const allClosing = hasLayers && layers.every((layer) => layer.status === LayerStatus.CLOSING);
    const offsets = calculateDrawerOffset(layers);
    const topOpenLayerIndex = layers.findLastIndex((layer) => layer.status !== LayerStatus.CLOSING);

    useEffect(() => {
        if (!hasLayers) {
            return;
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !event.defaultPrevented) {
                requestClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hasLayers, requestClose]);

    if (!hasLayers) {
        return null;
    }

    return (
        <RemoveScroll forwardProps enabled={!allClosing}>
            <Wrapper inert={allClosing} style={{ pointerEvents: allClosing ? 'none' : undefined }}>
                {layers.map((layer, index) => (
                    <Layer
                        key={layer.id}
                        layer={layer}
                        offset={offsets[index]}
                        index={index}
                        isTopLayer={index === topOpenLayerIndex}
                        isCovered={index < topOpenLayerIndex}
                    >
                        <Sentry.ErrorBoundary
                            fallback={({ error, componentStack, resetError }) => (
                                <AdvancedError
                                    error={error}
                                    stack={componentStack}
                                    resetError={resetError}
                                    variant="fluid"
                                />
                            )}
                        >
                            {layer.content}
                        </Sentry.ErrorBoundary>
                    </Layer>
                ))}
                <Backdrop data-testid="drawer-backdrop" $isClosing={allClosing} onClick={() => requestClose()} />
            </Wrapper>
        </RemoveScroll>
    );
};
